import { ArrowLeft, ArrowRight, Check, Download, Eye, Loader2 } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/app/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/app/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetTitle } from "@/app/components/ui/sheet";
import { cn } from "@/app/handlers/cn.utils";

import FitCheckPaywall from "../components/FitCheckPaywall";
import { useFitCheckAllowances } from "../config/allowances";
import { useTailoredResume, useTailorResume } from "../config/api";
import { ResumeThumb, ResumeViewer, ResumeViewerHandle } from "./ResumeFrame";
import { TailoredResume, TEMPLATES } from "./renderer";
import { SAMPLE_RESUME } from "./sampleResume";

interface TailorResumeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	userId: string;
	jobId: string;
	jobDetails: Record<string, unknown>;
	roleTitle: string;
}

function TailorResumeDialog({ open, onOpenChange, userId, jobId, jobDetails, roleTitle }: TailorResumeDialogProps) {
	const sampleDocs = useMemo(() => TEMPLATES.map((t) => ({ ...t, html: t.renderDocument(SAMPLE_RESUME) })), []);

	const [phase, setPhase] = useState<"choose" | "result">("choose");
	const [selectedId, setSelectedId] = useState(TEMPLATES[0].id);
	const [previewId, setPreviewId] = useState<string | null>(null);
	const [wantsResult, setWantsResult] = useState(false);

	const { data: existing, isLoading: existingLoading } = useTailoredResume(userId, jobId, open);
	const { mutate, data: generated, isError, reset } = useTailorResume();
	const allowances = useFitCheckAllowances();
	const startedRef = useRef(false);
	const genStartRef = useRef(0);

	// Generation is metered. A stored resume reuses for free; a new one for a job with no credits left
	// is blocked. We must know the allowance before the open effect runs, so a blocked user never
	// triggers a paid generation.
	const tailorBlocked =
		!allowances.isLoading &&
		!allowances.tailor.unlimited &&
		allowances.tailor.remaining === 0 &&
		!existingLoading &&
		!existing?.resume;

	const runTailor = () => {
		genStartRef.current = Date.now();
		posthog.capture("tailored_resume_generation_started", { jobId, jobTitle: roleTitle });
		mutate(
			{ userId, jobId, jobDetails },
			{
				onSuccess: (res) => {
					if (res.readable && res.resume) {
						posthog.capture("tailored_resume_generated", {
							jobId,
							jobTitle: roleTitle,
							durationMs: Date.now() - genStartRef.current,
							skillsChanged: res.diff?.skillsChanged,
							summaryChanged: res.diff?.summaryChanged,
						});
					} else {
						posthog.capture("tailored_resume_generation_failed", { jobId, jobTitle: roleTitle, reason: "unreadable" });
					}
				},
				onError: (error) =>
					posthog.capture("tailored_resume_generation_failed", { jobId, jobTitle: roleTitle, reason: error.message }),
			},
		);
	};

	// On open, reuse a stored tailored resume if one exists; otherwise generate a fresh one — unless the
	// tier gate blocks it. Waits for the allowance so a blocked user never starts a paid generation.
	useEffect(() => {
		if (!open || existingLoading || allowances.isLoading || startedRef.current) return;
		startedRef.current = true;
		if (existing?.resume) {
			posthog.capture("tailored_resume_reused", { jobId, jobTitle: roleTitle });
		} else if (tailorBlocked) {
			posthog.capture("tailored_resume_blocked", { jobId, jobTitle: roleTitle, tier: allowances.tier });
		} else {
			runTailor();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, existingLoading, allowances.isLoading, existing, tailorBlocked]);

	const reusedResume = existing?.resume;
	const freshResume = generated?.readable ? generated?.resume : undefined;
	const tailored = (reusedResume ?? freshResume) as unknown as TailoredResume | undefined;
	const ready = !!tailored;
	const unreadable = !reusedResume && !!generated && !generated.readable;
	const failed = isError || unreadable;

	const selected = TEMPLATES.find((t) => t.id === selectedId) ?? TEMPLATES[0];

	const goToResult = () => {
		posthog.capture("tailored_resume_template_selected", { jobId, jobTitle: roleTitle, template: selectedId });
		setPhase("result");
	};

	useEffect(() => {
		if (wantsResult && ready) goToResult();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wantsResult, ready]);

	const resultHtml = useMemo(() => (tailored ? selected.renderDocument(tailored) : ""), [tailored, selected]);
	const viewerRef = useRef<ResumeViewerHandle>(null);

	const handleOpenChange = (next: boolean) => {
		onOpenChange(next);
		if (!next) {
			startedRef.current = false;
			setPhase("choose");
			setWantsResult(false);
			setSelectedId(TEMPLATES[0].id);
			reset();
		}
	};

	const previewTemplate = sampleDocs.find((t) => t.id === previewId);

	return (
		<>
			<Sheet open={open} onOpenChange={handleOpenChange}>
				<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:!max-w-[920px]">
					{phase === "choose" && tailorBlocked ? (
						<div className="flex h-full flex-col">
							<div className="border-b border-gray-200 px-6 pb-4 pt-6">
								<SheetTitle className="font-plus-jakarta text-xl text-gray-900">Tailored resumes</SheetTitle>
								<SheetDescription className="text-sm text-gray-600">
									Tailor your resume for <span className="font-semibold text-gray-800">{roleTitle}</span>.
								</SheetDescription>
							</div>
							<div className="flex flex-1 items-center justify-center p-6">
								<FitCheckPaywall
									kind="tailor"
									allowance={allowances.tailor}
									tier={allowances.tier}
									onDismiss={() => handleOpenChange(false)}
								/>
							</div>
						</div>
					) : phase === "choose" ? (
						<>
							<div className="border-b border-gray-200 px-6 pb-4 pt-6">
								<SheetTitle className="font-plus-jakarta text-xl text-gray-900">Choose a template</SheetTitle>
								<SheetDescription className="text-sm text-gray-600">
									Pick a look for your resume, tailored for{" "}
									<span className="font-semibold text-gray-800">{roleTitle}</span>.
								</SheetDescription>
								<div className="mt-3">
									{failed ? (
										<span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm text-red-700">
											We could not tailor your resume.
											<button onClick={runTailor} className="font-semibold underline underline-offset-2">
												Try again
											</button>
										</span>
									) : ready ? (
										<span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
											<Check className="h-4 w-4" /> Your tailored resume is ready
										</span>
									) : (
										<span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600">
											<Loader2 className="h-4 w-4 animate-spin" /> Tailoring your resume in the background…
										</span>
									)}
								</div>
							</div>

							<div className="flex-1 overflow-y-auto px-6 py-6">
								<div className="flex flex-wrap justify-center gap-5">
									{sampleDocs.map((t, i) => {
										const isSelected = selectedId === t.id;
										return (
											<div
												key={t.id}
												style={{ animationDelay: `${i * 45}ms` }}
												className="flex flex-col gap-2 motion-safe:animate-fc-rise"
											>
												<button
													onClick={() => setSelectedId(t.id)}
													className={cn(
														"group relative overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:shadow-md",
														isSelected
															? "border-primary-700 ring-2 ring-primary-700"
															: "border-gray-200 hover:border-gray-300",
													)}
												>
													<ResumeThumb html={t.html} width={200} />
													{isSelected && (
														<span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-700 text-white shadow-sm motion-safe:animate-fc-pop">
															<Check className="h-4 w-4" />
														</span>
													)}
													<span
														onClick={(e) => {
															e.stopPropagation();
															posthog.capture("tailored_resume_template_previewed", {
																jobId,
																jobTitle: roleTitle,
																template: t.id,
															});
															setPreviewId(t.id);
														}}
														className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-gray-900/75 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100"
													>
														<Eye className="h-3.5 w-3.5" /> Preview
													</span>
												</button>
												<span
													className={cn("px-1 text-sm", isSelected ? "font-semibold text-gray-900" : "text-gray-600")}
												>
													{t.name}
												</span>
											</div>
										);
									})}
								</div>
							</div>

							<SheetFooter className="flex-row justify-end border-t border-gray-200 px-6 py-4">
								<Button
									onClick={() => (ready ? goToResult() : setWantsResult(true))}
									disabled={failed || (wantsResult && !ready)}
								>
									{wantsResult && !ready ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" /> Finishing your resume…
										</>
									) : (
										<>
											Continue with {selected.name} <ArrowRight className="ml-1 h-4 w-4" />
										</>
									)}
								</Button>
							</SheetFooter>
						</>
					) : (
						<>
							<div className="border-b border-gray-200 px-6 pb-4 pt-6 pr-14">
								<SheetTitle className="font-plus-jakarta text-lg text-gray-900">Your tailored resume</SheetTitle>
								<SheetDescription className="text-sm text-gray-600">
									{roleTitle} · {selected.name} template
								</SheetDescription>
							</div>

							<div className="flex-1 overflow-auto bg-gray-100 p-5">
								<ResumeViewer ref={viewerRef} html={resultHtml} className="mx-auto max-w-[816px] shadow-sm" />
							</div>

							<SheetFooter className="flex-row items-center justify-between border-t border-gray-200 px-6 py-4">
								<Button
									variant="secondary"
									onClick={() => {
										setWantsResult(false);
										setPhase("choose");
									}}
								>
									<ArrowLeft className="h-4 w-4 mr-2" /> Change template
								</Button>
								<Button
									onClick={() => {
										posthog.capture("tailored_resume_downloaded", {
											jobId,
											jobTitle: roleTitle,
											template: selectedId,
										});
										viewerRef.current?.print();
									}}
								>
									<Download className="h-4 w-4 mr-2" /> Download PDF
								</Button>
							</SheetFooter>
						</>
					)}
				</SheetContent>
			</Sheet>

			<Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
				<DialogContent className="max-h-[92vh] gap-0 overflow-hidden p-0 sm:max-w-[760px]">
					<DialogHeader className="flex-row items-center justify-between gap-3 border-b border-gray-200 py-3 pl-5 pr-14 text-left">
						<DialogTitle className="font-plus-jakarta text-base text-gray-900">
							{previewTemplate?.name} template
						</DialogTitle>
						<Button
							size="sm"
							onClick={() => {
								if (previewId) setSelectedId(previewId);
								setPreviewId(null);
							}}
						>
							Use this template
						</Button>
					</DialogHeader>
					<div className="max-h-[80vh] overflow-auto bg-gray-100 p-5">
						{previewTemplate && (
							<ResumeViewer html={previewTemplate.html} className="mx-auto max-w-[816px] shadow-sm" />
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default TailorResumeDialog;
